import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
  setContactFormModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { getNextBillDate } from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import tw from '@/common/utils/twmacro';
import { notifyZapier } from '@/common/utils/zapier';
import api from '@/db/apiClient';
import { ArrowRightIcon, InfoIcon } from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@shadcn/components/dialog';
import dayjs from 'dayjs';
import { useState } from 'react';
import { toast } from 'sonner';

interface ManageSubscriptionDialogueProps {
  onUpdateBillingAddress: () => void;
  children: React.ReactNode;
}

export default function ManageSubscriptionDialogue({
  onUpdateBillingAddress,
  children,
}: ManageSubscriptionDialogueProps) {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(selectUserId);
  const { organisation } = useOrganisation();

  const [isOpen, setIsOpen] = useState(false);

  const handleDialogOpen = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      trackMixpanelEvent('Clicked Manage Subscription', {
        source: 'billing_overview',
        currentPlan: license?.plan,
        currentStatus: licenseStatus,
      });
    }
  };

  const handleContactClick = () => {
    trackMixpanelEvent('Clicked Contact from Manage Subscription', {
      source: 'manage_subscription_dialog',
    });
    dispatch(setContactFormModalOpen(true));
  };

  const license = organisation?.license;
  const licenseStatus = license?.status;
  const stripeCustomerId = license?.stripeCustomerId;
  const stripeSubscriptionId = license?.stripeSubscriptionId;

  async function handleConfirmCancelSubscription() {
    if (!stripeCustomerId) {
      console.error('No stripe customer ID');
      toast.error('Failed to cancel subscription', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    if (licenseStatus !== 'active') {
      console.error('Subscription is not active');
      toast.error('Failed to cancel subscription', {
        description: 'Subscription is not active',
      });
      return;
    }
    setConfirmationDialogueCallback(async () => {
      try {
        if (
          !organisation ||
          !stripeSubscriptionId ||
          !organisation.license.billingInfo
        ) {
          toast.error('Failed to cancel subscription', {
            description: UNKNOWN_ERROR_MESSAGE,
          });
          return;
        }
        await api.asEducator.cancelSubscription({
          customerId: stripeCustomerId,
          subscriptionId: stripeSubscriptionId,
        });
        await notifyZapier({
          hook: 'subscription',
          event: 'Subscription Cancelled',
          userId: userId ?? 'Unknown',
          organisationId: organisation.id,
          organisationName: organisation.name,
          name: organisation.license.billingInfo.name,
          email: organisation.license.billingInfo.email,
          cancelAt: dayjs(
            getNextBillDate(
              organisation.license.billingCycle,
              organisation.license.billingCycleAnchor,
            ),
          ).format('DD/MM/YYYY h:mm a'),
        });
        toast.success('Cancellation confirmed', {
          description:
            'Your subscription will be cancelled at the end of the current billing cycle.',
        });
      } catch (e) {
        console.error('Error canceling subscription:', e);
        toast.error('Failed to cancel subscription', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Cancel Subscription',
        description: [
          'Are you sure you want to cancel your subscription?',
          'You will lose access to your account and data at the end of the current billing cycle. ' +
            "We'll hold onto your data after that in case you change your mind.",
          'If you have any questions or feedback, please reach out to rupert@spellcaster.education',
        ],
        variant: 'destructive',
      }),
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="text-sm">
        <DialogHeader>
          <DialogTitle>Manage Subscription</DialogTitle>
          <DialogDescription>
            Make changes to your subscription and billing information.
          </DialogDescription>
        </DialogHeader>

        <Alert className="flex items-center gap-2">
          <InfoIcon size={18} className="ml-1" />
          <div className="flex flex-col gap-2">
            <span>
              If you would like to modify your plan or update your payment
              method or billing email, please get in touch at{' '}
              <span
                className="underline cursor-pointer"
                onClick={handleContactClick}
              >
                rupert@spellcaster.education
              </span>
            </span>
          </div>
        </Alert>

        <div className="flex flex-col gap-4">
          <DialogClose asChild onClick={onUpdateBillingAddress}>
            <ManageSubscriptionOption>
              Update billing address
              <ArrowRightIcon size={16} />
            </ManageSubscriptionOption>
          </DialogClose>

          <DialogClose asChild onClick={handleConfirmCancelSubscription}>
            <ManageSubscriptionOption className="text-destructive">
              Cancel subscription
              <ArrowRightIcon size={16} />
            </ManageSubscriptionOption>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ManageSubscriptionOption = tw.span`font-medium flex items-center gap-2 hover:underline cursor-pointer w-fit`;
